/**
 * Post-auth funnel target — survives the signup → email-confirm → auto-login
 * chain, including the case where the confirm happens in a different tab
 * (localStorage crosses tabs; sessionStorage would not).
 *
 * Why: the purchase funnel (marketing buy-pass → /passes → auth) must ALWAYS
 * land the user back on /passes after auth. The ?returnTo= search param only
 * survives a single navigation; this store survives the whole chain.
 *
 * Policy (per Manuel, 2026-08-04):
 *   - If a target was explicitly saved (user came from /passes), use it.
 *   - Otherwise, users who haven't bought yet default to /passes (the funnel
 *     always converges on pricing until they own a pass).
 *   - Buyers default to /app.
 *
 * TTL 30 min — a stale target from last week shouldn't hijack a fresh login.
 */

const KEY = "tot_post_auth_target";
const TTL_MS = 30 * 60 * 1000;

export function savePostAuthTarget(path: string): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ path, at: Date.now() }));
  } catch { /* storage unavailable (private mode) — funnel default covers it */ }
}

export function takePostAuthTarget(): string | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    localStorage.removeItem(KEY);
    const { path, at } = JSON.parse(raw) as { path?: string; at?: number };
    if (!path || typeof at !== "number") return null;
    if (Date.now() - at > TTL_MS) return null;
    // Same-origin paths only (mirror login.tsx safeReturnTo policy).
    if (!path.startsWith("/") || path.startsWith("//")) return null;
    return path;
  } catch {
    return null;
  }
}

/**
 * Decide where to land after a successful auth.
 * Explicit saved target wins; otherwise buyers → fallback, non-buyers → /passes.
 * The has-pass check is done by the CALLER (it has the api client) — this
 * helper just centralizes the policy.
 */
export function resolvePostAuthTarget(opts: {
  savedTarget: string | null;
  explicitReturnTo?: string | null;
  hasActivePass: boolean;
  fallback?: string;
}): string {
  if (opts.savedTarget) return opts.savedTarget;
  if (opts.explicitReturnTo) return opts.explicitReturnTo;
  return opts.hasActivePass ? (opts.fallback ?? "/app") : "/passes";
}
